import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ModernProductContentManager } from "./ModernProductContentManager";
import { Layout, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useModernProductContent } from "@/hooks/useModernProductContent";
import { supabase } from "@/integrations/supabase/client";
import { ProductContentSection } from "@/types/modernProductContent";

interface ContentTabProps {
  productId: string;
  contentSections?: any[];
  onContentSectionsChange?: (sections: any[]) => void;
}

const ContentTab: React.FC<ContentTabProps> = ({
  productId,
  contentSections = [],
  onContentSectionsChange
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [localSections, setLocalSections] = useState<ProductContentSection[]>([]);
  const { data: productData, isLoading } = useModernProductContent(productId);

  // Update local sections when product data loads or content sections change
  React.useEffect(() => {
    if (productData?.sections) {
      setLocalSections(productData.sections);
    } else if (contentSections) {
      setLocalSections(contentSections);
    }
  }, [productData?.sections, contentSections]);

  const handleSaveSections = async () => {
    if (!productId) {
      toast({
        title: "Error",
        description: "Product ID is required to save content sections",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Delete existing sections
      await supabase
        .from('product_content_sections')
        .delete()
        .eq('product_id', productId);

      // Insert new sections
      if (localSections.length > 0) {
        const sectionsToInsert = localSections.map((section, index) => ({
          product_id: productId,
          section_type: section.section_type,
          title: section.title,
          subtitle: section.subtitle,
          content: section.content,
          layout_config: section.layout_config,
          display_order: index,
          is_active: true
        }));

        const { error } = await supabase
          .from('product_content_sections')
          .insert(sectionsToInsert);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Product content sections saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save content sections",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isNewProduct = !productId;

  if (isNewProduct) {
    return (
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-admin-foreground flex items-center gap-2">
              <Layout className="h-5 w-5" />
              Modern Product Content Management
            </CardTitle>
            <div className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded">
              Draft Mode
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              💡 You're creating content for a new product. Content sections will be saved when you save the product.
            </p>
          </div>
          <ModernProductContentManager
            productId="draft"
            sections={localSections}
            onSectionsChange={(newSections) => {
              setLocalSections(newSections);
              if (onContentSectionsChange) {
                onContentSectionsChange(newSections);
              }
            }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-admin-foreground flex items-center gap-2">
            <Layout className="h-5 w-5" />
            Modern Product Content Management
          </CardTitle>
          <Button
            onClick={handleSaveSections}
            disabled={isSaving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Content'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground">Loading content sections...</p>
        ) : (
          <ModernProductContentManager
            productId={productId}
            sections={localSections}
            onSectionsChange={setLocalSections}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ContentTab;